import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Sparkles } from "lucide-react";
import { portalApi, setPortalSession, isPortalAuthenticated } from "../../lib/portalApi";
import { Button } from "../../components/ui/button";
import { FormField } from "../../components/ui/form-field";
import { formatApiError } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";
import { LanguageToggle } from "../../components/LanguageToggle";

export default function PortalLogin() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isPortalAuthenticated()) nav("/portal", { replace: true });
  }, [nav]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await portalApi.post("/portal/login", { email, password });
      setPortalSession(data.access_token, data.client);
      toast.success(t("Olá, {name}!", { name: data.client.name }));
      nav("/portal", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Login falhou"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-4">
          <LanguageToggle compact testId="portal-login-language-toggle" />
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-blue-600 text-white items-center justify-center mb-3 shadow-md">
            <Sparkles size={28} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">{t("Portal do Cliente")}</h1>
          <p className="text-slate-600 mt-1">{t("Acesse seu histórico, recibos e orçamentos.")}</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl shadow-xl border border-slate-100 p-7 space-y-4"
          data-testid="portal-login-form"
        >
          <FormField label={t("E-mail")} required>
            <input
              data-testid="portal-login-email"
              type="email"
              autoComplete="email"
              className="cm-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormField>
          <FormField label={t("Senha")} required>
            <input
              data-testid="portal-login-password"
              type="password"
              autoComplete="current-password"
              className="cm-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormField>
          <Button
            data-testid="portal-login-submit"
            type="submit"
            disabled={loading}
            className="w-full"
          >
            <LogIn size={16} className="mr-2" />
            {loading ? t("Entrando…") : t("Entrar")}
          </Button>
          <p className="text-xs text-slate-500 text-center pt-2">
            {t("Não tem acesso? Solicite ao seu gestor para liberar o portal.")}
          </p>
        </form>
      </div>
    </div>
  );
}
